import React, { useState, useEffect, useCallback } from 'react';
import Presenter from './Presenter';

const API_URL = process.env.REACT_APP_PLATFORM_API_BASE_URL;

interface ISupportMessage {
    id: string;
    conversationId: string;
    businessId: string;
    businessName: string;
    senderType: 'business' | 'admin';
    senderEmail: string;
    senderName: string;
    text: string;
    createdAt: number;
}

interface IConversation {
    conversationId: string;
    businessId: string;
    businessName: string;
    lastMessage: string;
    lastMessageAt: number;
    lastSenderType: 'business' | 'admin';
}

const AdminSupportContainer: React.FC = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const [conversations, setConversations] = useState<IConversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<IConversation | null>(null);
    const [messages, setMessages] = useState<ISupportMessage[]>([]);
    const [replyText, setReplyText] = useState('');

    const [loadingConversations, setLoadingConversations] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sendingReply, setSendingReply] = useState(false);
    const [error, setError] = useState('');

    // Check if already authenticated
    useEffect(() => {
        const storedKey = sessionStorage.getItem('admin_api_key');
        if (storedKey) {
            setPassword(storedKey);
            setIsAuthenticated(true);
        }
    }, []);

    // Load conversations when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            loadConversations();
        }
    }, [isAuthenticated]);

    const ADMIN_PASSWORD = 'Marcos123!';

    const handlePasswordSubmit = async () => {
        if (!password.trim()) {
            setPasswordError('Ingresa la clave de admin');
            return;
        }

        if (password !== ADMIN_PASSWORD) {
            setPasswordError('Clave incorrecta');
            return;
        }

        // Password is correct
        sessionStorage.setItem('admin_api_key', password);
        setIsAuthenticated(true);
        loadConversations();
    };

    const loadConversations = useCallback(async () => {
        const apiKey = sessionStorage.getItem('admin_api_key');
        if (!apiKey) return;

        setLoadingConversations(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/admin/support/conversations`, {
                headers: {
                    'x-admin-api-key': apiKey
                }
            });

            if (response.status === 401) {
                sessionStorage.removeItem('admin_api_key');
                setIsAuthenticated(false);
                return;
            }

            if (!response.ok) {
                throw new Error('Error al cargar conversaciones');
            }

            const data = await response.json();
            setConversations(data.conversations || []);
        } catch (err: any) {
            setError(err.message || 'Error al cargar conversaciones');
        } finally {
            setLoadingConversations(false);
        }
    }, []);

    const loadMessages = useCallback(async (conversationId: string) => {
        const apiKey = sessionStorage.getItem('admin_api_key');
        if (!apiKey) return;

        setLoadingMessages(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/admin/support/conversations/${conversationId}`, {
                headers: {
                    'x-admin-api-key': apiKey
                }
            });

            if (!response.ok) {
                throw new Error('Error al cargar mensajes');
            }

            const data = await response.json();
            setMessages(data.messages || []);
        } catch (err: any) {
            setError(err.message || 'Error al cargar mensajes');
        } finally {
            setLoadingMessages(false);
        }
    }, []);

    const handleSelectConversation = (conversation: IConversation) => {
        setSelectedConversation(conversation);
        setReplyText('');
        loadMessages(conversation.conversationId);
    };

    const handleSendReply = async () => {
        if (!replyText.trim() || !selectedConversation) return;

        const apiKey = sessionStorage.getItem('admin_api_key');
        if (!apiKey) return;

        setSendingReply(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/admin/support/reply`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-admin-api-key': apiKey
                },
                body: JSON.stringify({
                    conversationId: selectedConversation.conversationId,
                    businessId: selectedConversation.businessId,
                    businessName: selectedConversation.businessName,
                    text: replyText.trim()
                })
            });

            if (!response.ok) {
                throw new Error('Error al enviar respuesta');
            }

            // Clear input and reload messages
            setReplyText('');
            await loadMessages(selectedConversation.conversationId);
            await loadConversations(); // Refresh list to update last message
        } catch (err: any) {
            setError(err.message || 'Error al enviar respuesta');
        } finally {
            setSendingReply(false);
        }
    };

    const handleCloseConversation = () => {
        setSelectedConversation(null);
        setMessages([]);
        setReplyText('');
    };

    const handleLogout = () => {
        sessionStorage.removeItem('admin_api_key');
        setIsAuthenticated(false);
        setPassword('');
        setConversations([]);
        setSelectedConversation(null);
        setMessages([]);
    };

    const formatTime = (timestamp: number) => {
        const date = new Date(timestamp * 1000);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) {
            return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        } else if (days === 1) {
            return 'Ayer';
        } else if (days < 7) {
            return `Hace ${days} dias`;
        } else {
            return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
        }
    };

    return (
        <Presenter
            isAuthenticated={isAuthenticated}
            password={password}
            passwordError={passwordError}
            onPasswordChange={setPassword}
            onPasswordSubmit={handlePasswordSubmit}
            conversations={conversations}
            selectedConversation={selectedConversation}
            messages={messages}
            replyText={replyText}
            onReplyChange={setReplyText}
            onSelectConversation={handleSelectConversation}
            onSendReply={handleSendReply}
            onCloseConversation={handleCloseConversation}
            onRefresh={loadConversations}
            onLogout={handleLogout}
            loadingConversations={loadingConversations}
            loadingMessages={loadingMessages}
            sendingReply={sendingReply}
            error={error}
            formatTime={formatTime}
        />
    );
};

export default AdminSupportContainer;
